import { REPOSITORY_INTELLIGENCE_MESSAGES } from '@ashniva/types';

import { ApiError } from '../../shared/lib/api-client';

/**
 * Why a Repository Intelligence request did not give an answer, in the screens' terms (MASTER-SPEC §14.4).
 *
 * Desk answers every refusal with a status and a fixed text of its own (`REPOSITORY_INTELLIGENCE_MESSAGES`), never
 * anything Repository Intelligence said. The screens read the status, and the fixed text only where the status alone
 * cannot tell two states apart — "switched off for the organization" and "not for you" are both 403 — and then show
 * their own words. A message is never displayed.
 */
export type RiProblem =
  /** The organization switched Repository Intelligence off. */
  | 'disabled'
  | 'ask-disabled'
  | 'analyze-disabled'
  /** This Desk server is not set up to reach Repository Intelligence. */
  | 'not-configured'
  /** Not the system Super Admin, or without the permission. */
  | 'forbidden'
  | 'signed-out'
  /** No such repository link in the organization. */
  | 'link-not-found'
  /** Nobody asked Repository Intelligence to index this repository. */
  | 'no-registration'
  /** Repository Intelligence does not have it: not indexed, or not in the index any more. */
  | 'not-found'
  /** Being prepared, not available right now, or already registered. */
  | 'conflict'
  | 'rate-limited'
  /** Repository Intelligence is down, slow, or refused Desk: try again later. */
  | 'unavailable'
  | 'invalid'
  | 'unprocessable'
  /** The person closed the password prompt. */
  | 'cancelled'
  | 'unknown';

const BY_MESSAGE = new Map<string, RiProblem>([
  [REPOSITORY_INTELLIGENCE_MESSAGES.ORGANIZATION_DISABLED, 'disabled'],
  [REPOSITORY_INTELLIGENCE_MESSAGES.ASK_DISABLED, 'ask-disabled'],
  [REPOSITORY_INTELLIGENCE_MESSAGES.ANALYZE_DISABLED, 'analyze-disabled'],
  [REPOSITORY_INTELLIGENCE_MESSAGES.NOT_CONFIGURED, 'not-configured'],
  [REPOSITORY_INTELLIGENCE_MESSAGES.LINK_NOT_FOUND, 'link-not-found'],
  [REPOSITORY_INTELLIGENCE_MESSAGES.NO_REGISTRATION, 'no-registration'],
]);

/** What the reauth prompt rejects with when the person cancels it (`useReauth`). */
const REAUTH_CANCELLED = 'Password confirmation cancelled';

export function classifyProblem(error: unknown): RiProblem {
  if (!(error instanceof ApiError)) {
    if (error instanceof Error && error.message === REAUTH_CANCELLED) {
      return 'cancelled';
    }
    // `fetch` rejects with a TypeError when the network is down: nothing reached Desk.
    return error instanceof TypeError ? 'unavailable' : 'unknown';
  }
  const known = BY_MESSAGE.get(error.body?.message ?? '');
  switch (error.status) {
    case 400:
      return 'invalid';
    case 401:
      return 'signed-out';
    case 403:
      return known ?? 'forbidden';
    case 404:
      return known ?? 'not-found';
    case 409:
      return 'conflict';
    case 422:
      return 'unprocessable';
    case 429:
      return 'rate-limited';
    case 503:
      return known ?? 'unavailable';
    default:
      return error.status >= 500 ? 'unavailable' : 'unknown';
  }
}

/** A problem worth a "Try again" button: it may well go away on its own. */
export function isRetryable(problem: RiProblem): boolean {
  return problem === 'unavailable' || problem === 'rate-limited' || problem === 'unknown';
}
